import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../services/api';

/**
 * El agente de cada empresa con AR, para ClaimUp (06-reparto §4.4).
 *
 * Qué ha soltado cada una en «se ejecuta sola» —lo que más conviene vigilar— y
 * el interruptor para parar su agente si algo va mal. Parado, el agente no coge
 * ninguna tarea de sus expedientes AR; lo de recobro sigue igual.
 * Ver docs/ar-ia-modo-automatico-plan.md.
 */
const pct = v => (v == null ? '—' : `${Math.round(v * 100)} %`);

/** Muchos rechazos solo si hay volumen: tres rechazos de cinco no dicen nada. */
const MIN_REVISADAS_ALERTA = 10;
const RECHAZOS_ALERTA = 0.3;

/**
 * Lo que mira ClaimUp de cada cliente (docs/ar-ia-kpis-supervision-plan.md):
 * lo que espera y si se atasca —un cliente que no revisa—, y si rechaza mucho
 * —la IA no le está acertando—. No aprueba nada: avisa.
 */
export function alertas(s) {
  if (!s) return [];
  const out = [];
  if (s.atascadas) out.push({ tono: 'red', texto: `${s.atascadas} propuestas esperan más de 48 h` });
  if (s.revisadas >= MIN_REVISADAS_ALERTA && s.rechazadas >= RECHAZOS_ALERTA) {
    out.push({ tono: 'amber', texto: `Rechaza el ${pct(s.rechazadas)} de lo que revisa` });
  }
  return out;
}

const ArCompaniesAgent = () => {
  const [empresas, setEmpresas] = useState([]);
  const [ocupada, setOcupada] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/ar-companies');
      setEmpresas(data.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async e => {
    let reason;
    if (!e.agent_paused) {
      // eslint-disable-next-line no-alert
      reason = window.prompt(`¿Por qué paras el agente de ${e.company_name}? Lo verá la empresa.`);
      if (reason === null) return;
    }
    setOcupada(e.company_id);
    try {
      await api.put(`/ai-agent/ar-companies/${e.company_id}/pause`, { paused: !e.agent_paused, reason });
      toast.success(e.agent_paused ? 'Agente reanudado' : 'Agente parado');
      await cargar();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOcupada(null);
    }
  };

  if (empresas.length === 0) return null;

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-4'>
      <p className='text-sm font-semibold text-slate-800 mb-1'>El agente de cada cliente AR</p>
      <p className='text-xs text-slate-500 mb-3'>
        Lo que cada uno deja salir sin revisar, y el interruptor para pararlo.
      </p>
      <div className='divide-y divide-slate-100'>
        {empresas.map(e => (
          <div key={e.company_id} className='flex flex-wrap items-center gap-3 py-2'>
            <span className='text-sm font-medium text-slate-800'>{e.company_name}</span>
            <span className='text-xs text-slate-500'>
              {e.automatic_actions?.length
                ? `Sale sola: ${e.automatic_actions.join(', ')}`
                : 'Lo revisa todo'}
            </span>
            {e.agent_paused && e.paused_reason && (
              <span className='text-xs text-red-700'>Parado: {e.paused_reason}</span>
            )}
            {e.supervision && (
              <span className='w-full text-xs text-slate-500'>
                {e.supervision.pendientes} pendientes · 7 días: {e.supervision.revisadas} revisadas,{' '}
                {pct(e.supervision.aprobadas_tal_cual)} tal cual, {pct(e.supervision.rechazadas)} rechazadas,{' '}
                {e.supervision.automaticas} salieron solas
              </span>
            )}
            {alertas(e.supervision).map(a => (
              <span
                key={a.texto}
                className={`text-xs font-medium ${a.tono === 'red' ? 'text-red-700' : 'text-amber-700'}`}
              >
                {a.texto}
              </span>
            ))}
            <button
              type='button'
              disabled={ocupada === e.company_id}
              onClick={() => alternar(e)}
              className={`ml-auto rounded-lg px-3 py-1.5 text-xs font-medium border disabled:opacity-50 ${
                e.agent_paused
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-slate-50 border-slate-200 text-slate-600'
              }`}
            >
              {e.agent_paused ? 'Parado · reanudar' : 'Activo · parar'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ArCompaniesAgent;
